import { ReactElement, useState } from 'react';
import { Chip, ExpandToggle, Tooltip, Typography } from '@weareopenr/component-library';
import classnames from 'classnames/bind';
import MenuSectionMeta from 'src/components/MenuSection/MenuSectionMeta';
import ImageIcon from 'src/assets/images/ImageIcon';
import { IAttributeRule, IChannelAvailability } from 'src/types/menu.types';
import { NO_DESTINATIONS_MESSAGE } from './constants';
import styles from './AttributeRulesTab.module.scss';

const cx = classnames.bind(styles);

export interface IProductDefaults {
  consumerName: string | null;
  description?: string | null;
  plu?: string | null;
  imageUrl: string | null;
}

interface IAttributeRuleCardProps {
  rule: IAttributeRule;
  defaults: IProductDefaults;
}

const getChannels = (rule: IAttributeRule): IChannelAvailability[] => {
  const channels = new Map<string, IChannelAvailability>();
  rule.destinations.forEach((destination) => {
    channels.set(destination.external_system_id, {
      externalSystemId: destination.external_system_id,
      name: destination.external_system_name,
    });
  });
  return [...channels.values()];
};

const getVenueCount = (rule: IAttributeRule): number =>
  new Set(rule.destinations.flatMap((destination) => destination.venues.map((venue) => venue.venue_id))).size;

const getDestinationLabels = (rule: IAttributeRule): string[] =>
  rule.destinations.flatMap((destination) =>
    destination.venues.map((venue) => `${venue.name} - ${destination.extension_instance_name}`),
  );

const RuleValue = ({
  heading,
  value,
  defaultValue,
}: {
  heading: string;
  value: string | null;
  defaultValue?: string | null;
}): ReactElement => (
  <div className={styles['value']}>
    <Typography size="medium" weight="bold">
      {heading}
    </Typography>
    {/* Only null inherits; a rule can set a value to an empty string */}
    {value !== null ? (
      <Typography size="medium">{value || '-'}</Typography>
    ) : (
      <Typography size="medium" className={styles['inherited']}>
        {defaultValue ? `Inherited - ${defaultValue}` : 'Inherited'}
      </Typography>
    )}
  </div>
);

const AttributeRuleCard = ({ rule, defaults }: IAttributeRuleCardProps): ReactElement => {
  const [isExpanded, setIsExpanded] = useState(true);

  const isPlaceholder = rule.destinations.length === 0;
  const ruleImageUrl = rule.assets[0]?.asset_variants[0]?.url;
  const imageUrl = ruleImageUrl ?? defaults.imageUrl;
  const destinationLabels = getDestinationLabels(rule);

  return (
    <div className={cx('card', { placeholder: isPlaceholder })} data-testid="attribute-rule-card">
      <div className={styles['header']}>
        <div className={styles['header-content']}>
          <div className={styles['title-row']}>
            <Typography size="large" weight="bold">
              {rule.name}
            </Typography>
            <ExpandToggle
              isExpanded={isExpanded}
              onClick={() => setIsExpanded(!isExpanded)}
              size="small"
              className={styles['expand-toggle']}
            />
          </div>
          <MenuSectionMeta
            subsectionCount={0}
            channels={getChannels(rule)}
            isChannelsUnavailable={isPlaceholder}
            venueCount={getVenueCount(rule)}
            isVenuesUnavailable={isPlaceholder}
            noChannelsMessage={NO_DESTINATIONS_MESSAGE}
            noVenuesMessage={NO_DESTINATIONS_MESSAGE}
          />
        </div>
        {isPlaceholder && (
          <Tooltip text={NO_DESTINATIONS_MESSAGE}>
            <Chip label="No Destinations" backgroundColour="tomato" icon="attention" />
          </Tooltip>
        )}
      </div>

      {isExpanded && (
        <div className={styles['body']}>
          <Typography size="large" weight="bold">
            Product Details:
          </Typography>
          <div className={styles['details']}>
            <div className={styles['column']}>
              <RuleValue heading="Consumer Name" value={rule.consumer_name} defaultValue={defaults.consumerName} />
              <RuleValue heading="Description" value={rule.description} defaultValue={defaults.description} />
              <RuleValue heading="POS ID" value={rule.plu} defaultValue={defaults.plu} />
            </div>
            <div className={styles['column']}>
              <div className={styles['value']}>
                <Typography size="medium" weight="bold">
                  Image
                </Typography>
                {!ruleImageUrl && (
                  <Typography size="medium" className={styles['inherited']}>
                    Inherited
                  </Typography>
                )}
                {imageUrl ? (
                  <img
                    className={cx('image', { 'inherited-image': !ruleImageUrl })}
                    src={imageUrl}
                    alt={`${rule.name} image`}
                  />
                ) : (
                  <div className={styles['no-image']}>
                    <ImageIcon />
                    <Typography size="medium" className={styles['inherited']}>
                      No Image
                    </Typography>
                  </div>
                )}
              </div>
            </div>
          </div>
          <Typography size="large" weight="bold">
            Publishable Destinations:
          </Typography>
          <Typography size="medium" className={styles['destinations']}>
            {destinationLabels.length ? destinationLabels.join(', ') : '-'}
          </Typography>
        </div>
      )}
    </div>
  );
};

export default AttributeRuleCard;
